import { ChevronLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { useEffect, useState } from 'react';
import { useInitiateFlutterwavePaymentMutation } from '../../store/api/apiSlice';
import { CheckType } from '../../types';

const getReadableError = (err: any, fallback = 'An unexpected error occurred') => {
    const serverMessage = err?.data?.message ?? err?.message ?? err;

    if (Array.isArray(serverMessage)) {
        return serverMessage.join(', ');
    }

    if (typeof serverMessage === 'string') {
        return serverMessage;
    }

    if (serverMessage && typeof serverMessage === 'object') {
        return serverMessage.message || serverMessage.error || JSON.stringify(serverMessage);
    }

    return fallback;
};

interface PaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack: () => void;
    checkType: CheckType;
    investigationId: string;
}

export default function PaymentModal({ isOpen, onBack, checkType, investigationId }: PaymentModalProps) {
    const [initiateFlutterwavePayment, { isLoading: isInitiating, error: initiateError }] =
        useInitiateFlutterwavePaymentMutation();

    const [paymentLink, setPaymentLink] = useState<string>('');
    const [amountValue, setAmountValue] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isRedirecting, setIsRedirecting] = useState(false);

    useEffect(() => {
        if (!isOpen || !investigationId || paymentLink) return;

        const redirectUrl = `${window.location.origin}/investigations?payment=success`;

        initiateFlutterwavePayment({
            checkType,
            redirectUrl,
            investigationId,
        })
            .unwrap()
            .then((response) => {
                if (response.paymentLink) setPaymentLink(response.paymentLink);
                if (response.amount) setAmountValue(response.amount);
            })
            .catch((err) => {
                console.error('[PaymentModal] Failed to initiate payment:', err);
                setErrorMessage(getReadableError(err, 'Failed to initiate payment.'));
            });
    }, [isOpen, investigationId, checkType, initiateFlutterwavePayment, paymentLink]);

    const handlePayClick = () => {
        if (!paymentLink) {
            setErrorMessage('Payment session not ready. Please try again.');
            return;
        }
        setErrorMessage('');
        setIsRedirecting(true);
        window.location.href = paymentLink;
    };

    const handleRetryInitiate = () => {
        setErrorMessage('');
        setPaymentLink('');
        setAmountValue(0);
    };

    if (!isOpen) return null;

    const packageName =
        checkType === CheckType.STANDARD ? 'Standard' : checkType === CheckType.ENHANCED ? 'Enhanced' : 'Pro';
    const displayAmount = amountValue
        ? new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(
              amountValue,
          )
        : '';

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/50 md:items-center">
            <div className="h-full w-full overflow-y-auto bg-white shadow-2xl md:h-screen md:w-[600px] md:rounded-l-3xl">
                <div className="flex h-full flex-col p-6 md:p-8">
                    {/* Header */}
                    <div className="mb-6 flex items-center gap-4">
                        <button
                            onClick={onBack}
                            disabled={isInitiating || isRedirecting}
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 transition-colors hover:bg-gray-200 disabled:opacity-50"
                        >
                            <ChevronLeft className="h-5 w-5 text-gray-600" />
                        </button>
                        <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">Payment</h2>
                    </div>

                    {/* Description */}
                    <p className="mb-6 text-sm leading-relaxed text-gray-600">
                        Complete payment to finalize your investigation request. Once payment is confirmed, the employee
                        invitation will be sent immediately.
                    </p>

                    {isInitiating ? (
                        <div className="flex flex-1 items-center justify-center">
                            <div className="text-center">
                                <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900"></div>
                                <p className="text-gray-600">Preparing checkout...</p>
                            </div>
                        </div>
                    ) : initiateError && !paymentLink ? (
                        <div className="flex flex-1 items-center justify-center">
                            <div className="max-w-md text-center">
                                <p className="mb-4 text-red-600">
                                    {errorMessage || 'Failed to prepare checkout. Please try again.'}
                                </p>
                                <Button
                                    onClick={handleRetryInitiate}
                                    className="bg-gray-900 text-white hover:bg-gray-800"
                                >
                                    Retry
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-1 flex-col">
                            <div className="flex-1 overflow-y-auto">
                                {/* Package Type */}
                                <div className="mb-6 rounded-2xl border border-gray-100 bg-gray-50 p-6">
                                    <h3 className="text-xl font-bold text-gray-900">{packageName} Check</h3>
                                    {displayAmount && (
                                        <p className="mt-2 text-2xl font-bold text-[#FF6B35]">{displayAmount}</p>
                                    )}
                                </div>

                                <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-6">
                                    <h4 className="mb-2 text-lg font-semibold text-gray-900">Secure Checkout</h4>
                                    <p className="text-sm text-gray-600">
                                        You&apos;ll be taken to Flutterwave&apos;s secure checkout page to complete
                                        payment with card, bank transfer, or USSD. After payment you&apos;ll be
                                        redirected back here.
                                    </p>
                                </div>

                                {errorMessage && (
                                    <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
                                        <p className="text-sm text-red-600">{errorMessage}</p>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="space-y-3 border-t border-gray-100 pt-6">
                                <Button
                                    type="button"
                                    onClick={handlePayClick}
                                    disabled={!paymentLink || isRedirecting}
                                    className="w-full rounded-xl bg-gray-900 py-6 text-base font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isRedirecting ? 'Redirecting...' : 'Pay with Flutterwave'}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={onBack}
                                    disabled={isRedirecting}
                                    className="w-full rounded-xl border border-gray-200 bg-white py-6 text-base font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Back to Package Selection
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
